import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import axios from "axios";

export const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Prosím zadajte svoju e-mailovú adresu");
      return;
    }

    setForgotLoading(true);
    try {
      // Add timeout to prevent hanging
      const response = await Promise.race([
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { 
          email: forgotEmail 
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000) // 30 second timeout
        )
      ]);
      
      // Show success message
      toast.success("Ak e-mail existuje, bolo odoslané nové heslo. Toto heslo nie je možné zmeniť.");
      
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      console.error('Forgot password error:', error);
      
      if (error.message === 'Request timeout') {
        toast.error("Požiadavka trvala príliš dlho. Skontrolujte prosím nastavenia SMTP.");
      } else {
        const errorMessage = error.response?.data?.detail || "Vyskytla sa chyba. Skúste to prosím znova.";
        toast.error(errorMessage);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Prosím vyplňte všetky polia");
      return;
    }

    setLoading(true);
    try {
      const result = await onLogin(email, password);
      if (result.success) {
        toast.success(`Vitajte späť!`);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Vyskytla sa neočakávaná chyba");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-sage-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 sm:w-32 h-24 sm:h-32 bg-cream-200 rounded-full opacity-30 blur-2xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 fade-in">
          <div className="flex justify-center items-center">
            <img 
              src="/useba-logo-new.png" 
              alt="uSeba Logo" 
              className="w-72 sm:w-96 h-auto object-contain"
            />
          </div>
        </div>

        {/* Login Form */}
        <div className="card p-6 sm:p-8 slide-up">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-charcoal-700 text-sm font-medium mb-2">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field w-full"
                placeholder="Zadajte e-mailovú adresu"
                required
              />
            </div>

            <div>
              <label className="block text-charcoal-700 text-sm font-medium mb-2">
                Heslo
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12 w-full"
                  placeholder="Zadajte vaše heslo"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-500 hover:text-sage-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {/* Forgot Password Link */}
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-sage-600 hover:text-sage-700 transition-colors"
                >
                  Zabudnuté heslo?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  <span>Prihlasujem...</span>
                </>
              ) : (
                <span>Prihlásiť sa</span>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8 text-charcoal-500 text-sm px-4">
          <p>© 2026 Regresná Terapia</p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setShowForgotPassword(false)}>
          <div className="card p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Obnovenie hesla</h3>
            <p className="text-charcoal-600 text-sm mb-4">
              Zadajte svoju e-mailovú adresu a my Vám pošleme nové heslo. Toto heslo nie je možné zmeniť.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-charcoal-700 text-sm font-medium mb-2">
                  E-mailová adresa
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="input-field w-full"
                  placeholder="Zadajte svoju e-mailovú adresu"
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="btn-secondary flex-1"
                  disabled={forgotLoading}
                >
                  Zrušiť
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="btn-primary flex-1 flex items-center justify-center space-x-2"
                >
                  {forgotLoading ? (
                    <>
                      <div className="spinner"></div>
                      <span>Odosielanie...</span>
                    </>
                  ) : (
                    <span>Odoslať nové heslo</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};